/* Nou × Klaviyo — shared subscribe handler
 *
 * Drop-in for any form. Mark with `data-klaviyo-form`, set `data-source` for
 * attribution, and add hidden inputs as `properties[<key>]` to pass custom
 * profile properties (e.g. product handle, product title).
 *
 * Forms with class `nou-waitlist-form` (or `data-show-summary`) get the
 * structured success summary (email / size / text alerts) and dispatch a
 * `nou:klaviyo-success` event so the waitlist modal can auto-dismiss.
 * Other forms get the plain success message — backwards compatible with
 * the simple inline notify form.
 *
 * Optional: include `<input type="tel" name="phone">` to capture phone and
 * subscribe to SMS marketing on the same Klaviyo list. Phone is normalized
 * to E.164 (+1 prefix) before send; invalid phones are silently skipped.
 *
 *   <form data-klaviyo-form data-source="coming-soon">
 *     <input type="email" name="email" required>
 *     <input type="hidden" name="properties[product_handle]" value="lula-mule">
 *     <button type="submit">Notify Me</button>
 *   </form>
 */
(function () {
  const KLAVIYO_COMPANY_ID = 'TE6bVJ';
  const KLAVIYO_LIST_ID = 'WwXyZx'; // SHOP NOU
  const KLAVIYO_API = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY_ID;
  const KLAVIYO_REVISION = '2024-10-15';
  const KLAVIYO_BIS_API = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + KLAVIYO_COMPANY_ID;

  function normalizePhone(raw) {
    if (!raw) return null;
    const digits = String(raw).replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
    return null; // invalid — skip rather than send malformed
  }

  /**
   * @param {string} email
   * @param {string} source
   * @param {Record<string, string>} properties
   * @param {string | null} phone
   * @param {string} [listId]
   */
  function buildPayload(email, source, properties, phone, listId) {
    /** @type {Record<string, any>} */
    const profileAttrs = { email: email };
    if (phone) {
      profileAttrs.phone_number = phone;
      profileAttrs.subscriptions = {
        email: { marketing: { consent: 'SUBSCRIBED' } },
        sms: { marketing: { consent: 'SUBSCRIBED' } }
      };
    }
    if (Object.keys(properties).length > 0) {
      profileAttrs.properties = properties;
    }
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source || 'website',
          profile: { data: { type: 'profile', attributes: profileAttrs } }
        },
        relationships: {
          list: { data: { type: 'list', id: listId || KLAVIYO_LIST_ID } }
        }
      }
    };
  }

  /**
   * @param {string} email
   * @param {string} source
   */
  function subscribeToNewsletter(email, source) {
    fetch(KLAVIYO_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(buildPayload(email, source, {}, null, KLAVIYO_LIST_ID))
    }).catch(function (err) {
      console.error('[nou-klaviyo] newsletter opt-in failed', err);
    });
  }

  function buildBackInStockPayload(email, variantId) {
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: { email: email } } },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId }
          }
        }
      }
    };
  }

  function showSuccess(form, summary) {
    const successMsg = form.getAttribute('data-success') || "You're on the list. We'll be in touch.";
    const wrap = document.createElement('div');
    wrap.className = 'nou-klaviyo-success';
    wrap.setAttribute('role', 'status');

    if (summary) {
      const heading = document.createElement('p');
      heading.className = 'nou-klaviyo-success-title';
      heading.textContent = successMsg;
      wrap.appendChild(heading);

      const meta = document.createElement('dl');
      meta.className = 'nou-klaviyo-success-meta';
      Object.keys(summary).forEach(function (key) {
        const dt = document.createElement('dt');
        dt.textContent = key;
        const dd = document.createElement('dd');
        dd.textContent = summary[key];
        meta.appendChild(dt);
        meta.appendChild(dd);
      });
      wrap.appendChild(meta);
    } else {
      wrap.textContent = successMsg;
    }

    form.replaceWith(wrap);
    wrap.dispatchEvent(new CustomEvent('nou:klaviyo-success', { bubbles: true }));
  }

  function showError(form, msg) {
    let errEl = form.querySelector('.nou-klaviyo-error');
    if (!errEl) {
      errEl = document.createElement('div');
      errEl.className = 'nou-klaviyo-error';
      errEl.setAttribute('role', 'alert');
      form.appendChild(errEl);
    }
    errEl.textContent = msg;
  }

  function gatherProperties(form) {
    /** @type {Record<string, string>} */
    const props = {};
    const source = form.getAttribute('data-source');
    if (source) props.signup_source = source;

    form.querySelectorAll('input[name^="properties["]').forEach(function (input) {
      const match = input.name.match(/^properties\[([^\]]+)\]$/);
      if (!match) return;
      const key = match[1];
      const value = (input.value || '').trim();
      if (value) props[key] = value;
    });
    return props;
  }

  async function submitToKlaviyo(form) {
    const emailInput = form.querySelector('input[type="email"]');
    if (!emailInput || !emailInput.value) {
      showError(form, 'Please enter a valid email.');
      return;
    }
    const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
    const originalLabel = submitBtn ? submitBtn.textContent : null;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting…';
    }

    const phoneInput = form.querySelector('input[type="tel"]');
    const phone = phoneInput ? normalizePhone(phoneInput.value) : null;
    const email = emailInput.value.trim();
    const properties = gatherProperties(form);
    const source = form.getAttribute('data-source') || 'website';
    const listId = form.getAttribute('data-list-id') || KLAVIYO_LIST_ID;
    const payload = buildPayload(email, source, properties, phone, listId);
    const newsletterOptIn = form.querySelector('input[name="newsletter_opt_in"]')?.checked || false;

    const summary = {};
    summary['email'] = email;
    if (properties.sizes_interested) summary['size'] = properties.sizes_interested;
    if (phone) summary['text alerts'] = 'on';

    try {
      // const res = await fetch(KLAVIYO_API, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     revision: KLAVIYO_REVISION
      //   },
      //   body: JSON.stringify(payload)
      // });

      const isBackInStock = form.hasAttribute('data-back-in-stock');

      let res;
      console.log('checking ===================', isBackInStock)
      if (isBackInStock) {
        const variantIdInput = form.querySelector('[data-nou-notify-variant-id]');
        const rawVariantId = variantIdInput ? variantIdInput.value : '';
        if (!rawVariantId) throw new Error('Missing variant for back-in-stock subscription.');

        res = await fetch(KLAVIYO_BIS_API, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(buildBackInStockPayload(email, rawVariantId))
        });
      } else {
        res = await fetch(KLAVIYO_API, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(payload)
        });
      }

      // Klaviyo returns 202 on success with no body.
      if (res.status === 202 || (res.status >= 200 && res.status < 300)) {
        if (newsletterOptIn && (isBackInStock || listId !== KLAVIYO_LIST_ID)) {
          subscribeToNewsletter(email, source);
        }
        const showWithSummary = form.hasAttribute('data-show-summary') || form.classList.contains('nou-waitlist-form');
        showSuccess(form, showWithSummary ? summary : null);
        return;
      }
      let detail = '';
      try {
        const errBody = await res.json();
        detail = (errBody.errors && errBody.errors[0] && errBody.errors[0].detail) || '';
      } catch (_) { /* no JSON body */ }
      console.error('[nou-klaviyo]', res.status, detail);
      showError(form, detail || 'Something went wrong. Please try again.');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalLabel;
      }
    } catch (err) {
      console.error('[nou-klaviyo] network error', err);
      showError(form, 'Network error. Please try again.');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalLabel;
      }
    }
  }

  function findKlaviyoForm(e) {
    let form = (e.target && e.target.closest) ? e.target.closest('form[data-klaviyo-form]') : null;
    if (form) return form;
    // Shadow DOM fallback — submit events retarget at the shadow boundary,
    // so e.target won't descend into the form. composedPath() returns the
    // full event path including elements inside shadow trees.
    if (typeof e.composedPath === 'function') {
      const path = e.composedPath();
      for (let i = 0; i < path.length; i++) {
        const el = path[i];
        if (el && el.matches && el.matches('form[data-klaviyo-form]')) return el;
      }
    }
    return null;
  }

  function onSubmit(e) {
    if (e._nouKlaviyoHandled) return;
    const form = findKlaviyoForm(e);
    if (!form) return;
    e._nouKlaviyoHandled = true;
    e.preventDefault();
    submitToKlaviyo(form);
  }

  // Capture-phase listener runs before any other handler can stopPropagation.
  // Bubble-phase listener is a backup for cases where capture is intercepted.
  // _nouKlaviyoHandled flag prevents double-firing across the two phases.
  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('submit', onSubmit, false);

  console.log('[nou-klaviyo] handler attached');
})();
